import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { RecrystaMark } from "@/components/RecrystaLogo";

// Single source of truth: visible answers AND JSON-LD read from this array,
// so the schema can never drift from what's shown on the page.
const FAQS = [
  {
    q: "What is Recrysta?",
    a: "Recrysta is an AI revenue analyst for growing B2B sales teams. It connects to your HubSpot in under 10 minutes and delivers a weighted revenue forecast built from your own historical win rates (not CRM default probabilities), early warnings on deals going stale with suggested next actions, funnel leak analysis, per-rep performance, data health scoring, and a short AI-written briefing emailed every Monday that names the specific deals that moved your number and why.",
  },
  {
    q: "Who is Recrysta for?",
    a: "Recrysta is built for growing B2B sales teams — typically 5 to 25 reps — that run on HubSpot and don't have a dedicated RevOps hire. It's for founders, sales leaders, and revenue operations owners who need reliable forecasting and pipeline visibility without a six-figure enterprise contract or a long implementation project.",
  },
  {
    q: "How is Recrysta different from HubSpot's native forecast?",
    a: "HubSpot's default forecast typically multiplies each deal by a static stage probability that's rarely revisited. Recrysta computes a weighted forecast from your own historical win rates by stage, flags deals with problems such as past close dates or inactivity, retains a history of how your forecast changes week over week (which HubSpot doesn't natively keep), and delivers a plain-language weekly briefing explaining what moved and what needs action.",
  },
  {
    q: "How is Recrysta different from Clari, Gong, or Kluster?",
    a: "Those are enterprise revenue platforms — typically six-figure annual contracts, minimum seat counts, and implementation projects — generally not reachable for small teams. Recrysta delivers the core revenue clarity those tools are known for, but self-serve, from €99 per month, with a 10-minute setup and no implementation project, specifically for teams of 5 to 25 reps.",
  },
  {
    q: "What CRM does Recrysta work with?",
    a: "Recrysta currently integrates with HubSpot. Support for additional CRMs such as Salesforce and Pipedrive is on the roadmap but not yet available.",
  },
  {
    q: "How long does Recrysta take to set up?",
    a: "Under 10 minutes. You connect HubSpot with read-only access via OAuth, confirm a suggested mapping of your deal stages, and your dashboard and first forecast are ready. No implementation project, nothing for your reps to install.",
  },
  {
    q: "How much does Recrysta cost?",
    a: "Recrysta is self-serve with plans from €99 per month. There's a 14-day free trial with no credit card required. Pricing scales by team size across tiers up to €299 per month.",
  },
  {
    q: "Is my data safe with Recrysta?",
    a: "Recrysta uses read-only access to HubSpot and is EU-hosted. HubSpot tokens are encrypted. Only pseudonymized, minimal data (deal names, amounts, stages, and dates) is sent to the AI for the briefing — no contact personal information. The AI operates under no-training terms, so your data isn't used to train AI models, and a Data Processing Agreement is available.",
  },
  {
    q: "Does the AI make up the numbers?",
    a: "No. Every metric — the forecast, funnel conversions, per-rep performance, and deal risk — is computed in Recrysta's own code with fixed definitions before the AI ever sees it. The AI's role is to narrate what the data shows; it never calculates or invents a figure. That's why the weekly briefing can be trusted and acted on without re-checking it against the CRM.",
  },
  {
    q: "What is the Monday briefing?",
    a: "A short, one-page email Recrysta sends every Monday morning. It states your current weighted forecast, explains why the number moved since last week by naming the specific deals responsible, and lists the two to four deals that need action that day with a concrete suggested next step for each. The delivery schedule is configurable.",
  },
  {
    q: "Do sales reps get access, or is it only for leadership?",
    a: "Recrysta isn't only a leadership tool. Its Team view shows per-rep performance, quota progress, forecast reliability, and the deals needing attention — giving the whole team visibility into what needs action, not just a single number for the CEO.",
  },
  {
    q: "What does \"weighted forecast from your own win rates\" mean?",
    a: "Instead of HubSpot's default stage probabilities, Recrysta calculates each pipeline stage's actual historical win rate from your closed-won and closed-lost deals, then applies those real rates to your open pipeline. This produces a forecast grounded in how your deals have actually converted — usually more conservative and more accurate than CRM defaults.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Recrysta is month-to-month with no long-term contract. You can cancel from the billing page at any time and keep access through the end of the current billing period.",
  },
  {
    q: "Do you offer a DPA?",
    a: "Yes. A Data Processing Agreement is available on request for teams that require one for GDPR compliance. Email support@recrysta.com and we'll send it within one business day.",
  },
  {
    q: "What's on Recrysta's roadmap?",
    a: "Planned features include a HubSpot App Marketplace listing, a Friday \"bright spot\" digest, a board-pack export for founders reporting to investors, an \"Ask your pipeline\" chat that answers from your own computed metrics, team logins and per-rep briefings, multi-pipeline support, Slack delivery, and additional CRM integrations such as Salesforce and Pipedrive. These are directional and not yet available.",
  },
  {
    q: "Where is Recrysta based and hosted?",
    a: "Recrysta is EU-hosted, with all data stored in the European Union. It was founded in 2026 by a founder with a background in B2B financial planning and revenue operations.",
  },
];

const buildJsonLd = () => JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
});

export default function FaqPage() {
  // Inject FAQPage JSON-LD + set title/description while mounted; clean up on unmount.
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "FAQ — Recrysta";

    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content");
    desc?.setAttribute(
      "content",
      "Frequently asked questions about Recrysta — pricing, HubSpot integration, forecast methodology, EU hosting, AI privacy, DPA, and roadmap."
    );

    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute("href");
    canonical?.setAttribute("href", "https://recrysta.com/faq");

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-jsonld";
    script.textContent = buildJsonLd();
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      if (prevDesc && desc) desc.setAttribute("content", prevDesc);
      if (prevCanonical && canonical) canonical.setAttribute("href", prevCanonical);
      document.getElementById("faq-jsonld")?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="faq-page">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="faq-back-home">
            <RecrystaMark size={32} />
            <span className="text-sm font-bold tracking-tight">Recrysta</span>
          </Link>
          <Link
            to="/"
            className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
        <div className="text-center mb-14">
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">
            <span className="text-emerald-500/70">/</span>faq
          </div>
          <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
            Recrysta — Frequently Asked Questions
          </h1>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Know what you&rsquo;ll really close. An AI revenue analyst for growing HubSpot sales teams.
          </p>
        </div>

        <div className="space-y-8">
          {FAQS.map(({ q, a }, i) => (
            <section
              key={i}
              className="border-b border-slate-200 pb-8 last:border-b-0"
              data-testid={`faq-item-${i}`}
            >
              <h2 className="text-lg font-bold text-slate-900 mb-3">{q}</h2>
              <p className="text-sm text-slate-600 leading-relaxed">{a}</p>
            </section>
          ))}
        </div>

        <div className="mt-14 text-center">
          <p className="text-sm text-slate-600 mb-4">
            Have another question? Email{" "}
            <a href="mailto:support@recrysta.com" className="text-emerald-700 font-semibold underline">
              support@recrysta.com
            </a>
            {" "}— we reply within one business day.
          </p>
          <Link
            to="/signup"
            data-testid="faq-cta-signup"
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-5 py-3 text-sm font-bold hover:bg-slate-800 transition-colors"
          >
            Start free 14-day trial
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500">
          <span>&copy; Recrysta {new Date().getFullYear()}</span>
          <Link to="/privacy" className="hover:text-slate-900">Privacy</Link>
          <Link to="/imprint" className="hover:text-slate-900">Imprint</Link>
          <Link to="/faq" className="hover:text-slate-900">FAQ</Link>
        </div>
      </footer>
    </div>
  );
}
